import { Flex, Textarea, TextInput } from "@mantine/core";
import { type ChangeEvent, type ReactNode, useMemo } from "react";

import { QuestionWarningAlert } from "../../question-warning-alert";
import {
  FIELD_CONFIG,
  getSharedInputClassNames,
} from "./assessment-question-field-common";
import { AssessmentQuestionFieldLabel } from "./assessment-question-field-label";
import {
  AssessmentQuestionFields,
  type QuestionWarning,
  type GenerateButtonState,
} from "@/types/screener-builder";
import type { SingleOrArray } from "@/types/utils";

interface AssessmentQuestionFieldProps<Field extends AssessmentQuestionFields> {
  error?: ReactNode;
  field: Field;
  generateButtonState: GenerateButtonState<Field>;
  loading: boolean;
  onChange: (value: string) => void;
  onGenerate: () => void;
  value: string;
  warning?: SingleOrArray<QuestionWarning>;
}

export const AssessmentQuestionField = <
  Field extends AssessmentQuestionFields,
>({
  error,
  field,
  generateButtonState,
  loading,
  onChange,
  onGenerate,
  value,
  warning,
}: AssessmentQuestionFieldProps<Field>) => {
  const { helpLink, labelText } = useMemo(() => FIELD_CONFIG[field], [field]);
  const inputProps = useMemo(() => {
    const label = (
      <AssessmentQuestionFieldLabel
        field={field}
        helpLink={helpLink}
        labelText={labelText}
        generateButtonState={generateButtonState}
        loading={loading}
        onGenerate={onGenerate}
      />
    );

    return {
      classNames: getSharedInputClassNames(
        field !== AssessmentQuestionFields.Competency,
      ),
      disabled: loading,
      error,
      label,
      onChange: (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        onChange(event.currentTarget.value);
      },
      value,
    };
  }, [
    error,
    field,
    generateButtonState,
    helpLink,
    labelText,
    loading,
    onChange,
    onGenerate,
    value,
  ]);

  return (
    <Flex direction="column">
      {field === AssessmentQuestionFields.Competency ? (
        <TextInput {...inputProps} />
      ) : (
        <Textarea {...inputProps} />
      )}
      <QuestionWarningAlert warning={warning} />
    </Flex>
  );
};
